import { CardListSkeleton } from '@/components/skeletonLoaders';
import { ProjectDatasetsQuery, ProjectDatasetsQueryVariables } from '@/gql/graphql';
import useQuery from '@/hooks/useQuery';
import { DatasetResult } from '@/routes/dataset/datasetResult';
import { fragmentManager } from '@/services/fragmentManager';
import { useProjectKeyLoaderData } from '.';
import { HelpLine } from '../../../../components/helpText';
import { NoResultsTab } from '../components/noResultsTab';
import { FormattedMessage } from 'react-intl';
import { useNotifyOfPartialDataIfErrors } from '@/routes/rootErrorPage';
import { ErrorMessage } from '@/components/errorMessage';

fragmentManager.register(/* GraphQL */ `
  fragment ProjectDatasetsTab on Query {
    gbifProject(id: $key) {
      projectId
    }
    datasetsHelp: help(identifier: "how-to-link-datasets-to-my-project-page") {
      title
    }
  }
`);

const DATASET_QUERY = /* GraphQL */ `
  query ProjectDatasets($projectId: ID!) {
    datasetSearch(projectId: [$projectId], limit: 500) {
      count
      limit
      offset
      results {
        ...DatasetStubResult
      }
    }
  }
`;

export function ProjectDatasetsTab() {
  const { resource, datasetsHelp } = useProjectKeyLoaderData();

  // Can't happen but TS doesn't know
  if (resource?.__typename !== 'GbifProject') throw new Error('500');

  const projectId = resource?.projectId;

  const datasets = useQuery<ProjectDatasetsQuery, ProjectDatasetsQueryVariables>(DATASET_QUERY, {
    throwAllErrors: false,
    lazyLoad: !projectId, // Skip query if no projectId
    variables: {
      projectId: projectId ?? '',
    },
  });
  useNotifyOfPartialDataIfErrors(datasets.error);

  if (datasets.loading)
    return (
      <div className="g-pt-4 g-max-w-3xl g-m-auto">
        <CardListSkeleton />
      </div>
    );

  // If no projectId, show the NoResultsTab
  if (!projectId || !datasets.data || datasets.data?.datasetSearch?.results?.length === 0) {
    return (
      <div className="g-pt-4 g-max-w-3xl g-m-auto">
        <p className="g-pb-4 g-text-gray-600 g-text-sm g-text-end">
          <HelpLine title={datasetsHelp?.title} id="how-to-link-datasets-to-my-project-page" icon />
        </p>

        <NoResultsTab>
          <FormattedMessage id="cms.resource.noDatasets" />
          {datasets.error && (
            <ErrorMessage>
              <FormattedMessage id="phrases.loadError" />
            </ErrorMessage>
          )}
        </NoResultsTab>
      </div>
    );
  }

  return (
    <div className="g-pt-4 g-max-w-3xl g-m-auto">
      <div className="g-flex">
        <FormattedMessage
          id="counts.nDatasets"
          values={{ total: datasets.data.datasetSearch.count }}
          defaultMessage={`${datasets.data.datasetSearch.results.length} datasets`}
        />
        <p className="g-flex-grow g-pb-4 g-text-gray-600 g-text-sm g-text-end">
          <HelpLine title={datasetsHelp?.title} id="how-to-link-datasets-to-my-project-page" icon />
        </p>
      </div>

      {datasets.data.datasetSearch.results.length === 0 && (
        <NoResultsTab>
          <FormattedMessage id="cms.resource.noDatasets" />
        </NoResultsTab>
      )}

      {datasets.data.datasetSearch.results.map((item) => (
        <DatasetResult key={item.key} dataset={item} />
      ))}
    </div>
  );
}
